/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import axios from "axios";

// @mui material components
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ComplexProjectCard from "examples/Cards/ProjectCards/ComplexProjectCard";

// Project page components
import Header from "./components/Header";

// Images
import team1 from "assets/images/team-1.jpg";
import team2 from "assets/images/team-2.jpg";
import team3 from "assets/images/team-3.jpg";
import team4 from "assets/images/team-4.jpg";
import team5 from "assets/images/team-5.jpg";
import { API_ESA_OFFICE } from "../../../utils";
import logoESA from "assets/images/ESAOFFICE_LOGO.png";
import logorote from "assets/images/logorote.png";

function AllApplications() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use direct URL to public folder as fallback
  const logoESAUrl = logoESA || "/ESAOFFICE_LOGO.png";
  const logoroteUrl = logorote || "/logorote.png";

  // Fetch data from API
  useEffect(() => {
    const fetchApplications = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${API_ESA_OFFICE}aplikasi-terintegrasi`
        );

        if (response.data.success === 1) {
          setApplications(response.data.data);
        } else {
          setError("Gagal mengambil data aplikasi");
        }
      } catch (err) {
        console.error("Error fetching applications:", err);
        setError("Terjadi kesalahan saat mengambil data aplikasi");
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, []);

  // Function to handle opening application link
  const openApplication = (link) => {
    if (link) {
      window.open(link, "_blank");
    }
  };

  // Function to format date to Indonesian format
  const formatDateIndonesia = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    const options = {
      day: "numeric",
      month: "short",
      year: "numeric",
    };

    return date.toLocaleDateString("id-ID", options);
  };

  // Function to get random image for each application

  // Function to get random members for each application
  const getRandomMembers = () => {
    const allMembers = [team1, team2, team3, team4, team5];
    const memberCount = Math.floor(Math.random() * 3) + 2; // 2-4 members
    return allMembers.slice(0, memberCount);
  };

  // Function to get random date
  const getRandomDate = () => {
    const dates = ["02.03.22", "22.11.21", "06.03.20", "14.03.24", "16.01.22"];
    return dates[Math.floor(Math.random() * dates.length)];
  };

  if (loading) {
    return (
      <DashboardLayout>
        <MDBox width="calc(100% - 48px)" position="absolute" top="1.75rem">
          <DashboardNavbar light absolute />
        </MDBox>
        <Header profile={logoESAUrl} />
        <MDBox
          pb={3}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <MDTypography variant="h6" color="text.secondary">
            Memuat aplikasi...
          </MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <MDBox width="calc(100% - 48px)" position="absolute" top="1.75rem">
          <DashboardNavbar light absolute />
        </MDBox>
        <Header profile={logoESAUrl} />
        <MDBox
          pb={3}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <MDTypography variant="h6" color="error">
            {error}
          </MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <MDBox width="calc(100% - 48px)" position="absolute" top="1.75rem">
        <DashboardNavbar light absolute />
      </MDBox>
      <Header profile={logoESAUrl} />
      <MDBox pb={3}>
        <Grid container alignItems="center">
          <Grid item xs={12} md={7}>
            <MDBox mb={1}>
              <MDTypography variant="h5">
                Ekosisitem Kerja Digital - ESA OFFICE
              </MDTypography>
            </MDBox>
            <MDBox mb={2}>
              <MDTypography variant="body2" color="text">
                ESA OFFICE adalah platform kerja digital terintegrasi yang
                membangun interoperabilitas antar sistem untuk mendukung tata
                kelola pemerintahan yang efisien dan akuntabel.
                Aplikasi-aplikasi yang terhubung mendukung fungsi pelaporan,
                pelayanan publik, dan manajemen data secara sinkron lintas
                perangkat daerah.
              </MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={12} md={5} sx={{ textAlign: "right" }}>
            <MDButton variant="gradient" color="info">
              <Icon>add</Icon>&nbsp; Add New
            </MDButton>
          </Grid>
        </Grid>
        <MDBox mt={5}>
          <Grid container spacing={3}>
            {applications.map((app, index) => {
              return (
                <Grid item xs={12} md={6} lg={4} key={app.id}>
                  <MDBox mb={1.5} mt={1.5}>
                    <ComplexProjectCard
                      image={logoroteUrl}
                      title={app.nama_aplikasi}
                      description={
                        app.deskripsi ||
                        "Aplikasi terintegrasi untuk mendukung layanan digital pemerintahan daerah."
                      }
                      dateTime={formatDateIndonesia(app.dibuat)}
                      members={getRandomMembers()}
                      dropdown={{
                        action: () => openApplication(app.link),
                      }}
                    />
                  </MDBox>
                </Grid>
              );
            })}
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default AllApplications;
